import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useStaffAuth } from '../context/StaffAuthContext';
import { 
  ArrowLeft, MapPin, Clock, Phone, Mail, User, Calendar,
  Play, Pause, CheckCircle, AlertCircle, Loader2,
  ExternalLink, FileText, Home, MessageSquare, Save
} from 'lucide-react';
import axios from 'axios';
import StaffAirbnbChecklist, { isAirbnbJob, checklistComplete } from '../components/StaffAirbnbChecklist';
import StaffJobPhotos from '../components/StaffJobPhotos';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StaffJobDetails = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { token, isAuthenticated, loading: authLoading } = useStaffAuth();
  
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [staffNotes, setStaffNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesMessage, setNotesMessage] = useState('');
  const [checklistOk, setChecklistOk] = useState(true);
  const onChecklistChange = useCallback((cfg, state) => setChecklistOk(checklistComplete(cfg, state)), []);
  
  // Timer state
  const [timerRunning, setTimerRunning] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const timerRef = useRef(null);

  // Check auth status
  useEffect(() => {
    // Only redirect if auth loading is complete AND not authenticated
    if (!authLoading) {
      if (!isAuthenticated) {
        navigate('/staff/login');
      }
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Fetch job details when auth is ready
  const fetchJobDetails = useCallback(async () => {
    if (!token || !jobId) return;
    
    try {
      const response = await axios.get(`${API}/staff/jobs/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJob(response.data);
      setStaffNotes(response.data.staff_notes || '');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load job details');
    } finally {
      setLoading(false);
    }
  }, [token, jobId]);

  useEffect(() => {
    // Only fetch if authenticated and not still loading auth
    if (!authLoading && isAuthenticated && token && jobId) {
      fetchJobDetails();
    }
  }, [authLoading, isAuthenticated, token, jobId, fetchJobDetails]);

  // Timer effect - runs the countdown
  useEffect(() => {
    let interval = null;
    if (timerRunning) {
      interval = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [timerRunning]);

  // Load saved timer state: server first (survives logout / other device), then localStorage
  const timerLoadedRef = useRef(false);
  useEffect(() => {
    if (!jobId || !job || timerLoadedRef.current) return;
    timerLoadedRef.current = true;

    const applyState = (elapsed, running, start) => {
      if (running && start) {
        const additionalTime = Math.floor((Date.now() - start) / 1000);
        setElapsedTime((elapsed || 0) + additionalTime);
        setTimerRunning(true);
        setStartTime(start);
      } else {
        setElapsedTime(elapsed || 0);
        setTimerRunning(false);
        setStartTime(null);
      }
    };

    const ts = job.timer_state;
    if (ts && !ts.finished) {
      const start = ts.running && ts.started_at ? new Date(ts.started_at).getTime() : null;
      applyState(ts.elapsed_before || 0, !!ts.running, start);
      return;
    }
    if (job.status === 'completed') {
      setElapsedTime(job.time_worked_seconds || 0);
      return;
    }
    const savedTimer = localStorage.getItem(`job_timer_${jobId}`);
    if (savedTimer) {
      try {
        const { elapsed, running, start } = JSON.parse(savedTimer);
        applyState(elapsed, running, start);
      } catch (e) {
        console.error('Error loading timer state:', e);
      }
    }
  }, [jobId, job]);

  // Save timer state to localStorage (only after initial load, to avoid overwriting with zeros)
  useEffect(() => {
    if (!jobId || !timerLoadedRef.current) return;
    
    localStorage.setItem(`job_timer_${jobId}`, JSON.stringify({
      elapsed: elapsedTime,
      running: timerRunning,
      start: startTime
    }));
  }, [elapsedTime, timerRunning, startTime, jobId]);

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Record work session to backend
  const recordWorkSession = async (action, notes = null) => {
    try {
      await axios.post(`${API}/staff/work-sessions`, {
        job_id: jobId,
        job_type: job?.job_type || 'appointment',
        action: action,
        elapsed_seconds: elapsedTime,
        notes: notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log(`Work session recorded: ${action}`);
    } catch (err) {
      console.error('Failed to record work session:', err);
    }
  };

  const startTimer = async () => {
    console.log('Starting timer...');
    setStartTime(Date.now());
    setTimerRunning(true);
    
    // Record start session
    await recordWorkSession('start');
    
    // Update job status to in_progress
    try {
      await axios.patch(`${API}/staff/jobs/${jobId}/status`, 
        { status: 'in_progress' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setJob(prev => ({ ...prev, status: 'in_progress' }));
    } catch (err) {
      console.error('Failed to update job status:', err);
    }
  };

  const pauseTimer = async () => {
    console.log('Pausing timer...');
    setTimerRunning(false);
    setStartTime(null);
    
    // Record pause session
    await recordWorkSession('pause');
  };

  const finishJob = async () => {
    if (isAirbnbJob(job) && !checklistOk) {
      setError('Complete a checklist Airbnb e as fotos finais antes de terminar. / Complete the Airbnb checklist and final photos before finishing.');
      return;
    }
    // Pause timer if running
    if (timerRunning) {
      setTimerRunning(false);
      setStartTime(null);
    }
    
    // Record finish session (this sends notification to admin)
    await recordWorkSession('finish', staffNotes || null);
    
    // Update job status to completed with elapsed time
    try {
      await axios.patch(`${API}/staff/jobs/${jobId}/status`, 
        { status: 'completed', elapsed_time: elapsedTime },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setJob(prev => ({ ...prev, status: 'completed' }));
      // Clear the timer from localStorage after completion
      localStorage.removeItem(`job_timer_${jobId}`);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : (d?.code === 'airbnb_checklist_incomplete' ? 'Checklist Airbnb incompleta — complete todos os itens e fotos.' : 'Failed to complete job'));
    }
  };

  const saveStaffNotes = async () => {
    setSavingNotes(true);
    setNotesMessage('');
    
    try {
      await axios.patch(
        `${API}/staff/jobs/${jobId}/notes?notes=${encodeURIComponent(staffNotes)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotesMessage('Notes saved!');
      setTimeout(() => setNotesMessage(''), 3000);
    } catch (err) {
      setNotesMessage('Error saving notes');
    } finally {
      setSavingNotes(false);
    }
  };

  const updateJobStatus = async (newStatus) => {
    try {
      await axios.patch(`${API}/staff/jobs/${jobId}/status`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setJob(prev => ({ ...prev, status: newStatus }));
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update status');
    }
  };

  const openInMaps = () => {
    if (!job) return;
    
    // Build the address query - prioritize eircode for Ireland
    let query = '';
    if (job.eircode) {
      query = job.eircode;
    } else if (job.address) {
      query = job.address;
    }
    
    if (query) {
      // Use Google Maps URL scheme
      const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
      window.open(mapsUrl, '_blank');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-700';
      case 'in_progress': return 'bg-blue-100 text-blue-700';
      case 'accepted': return 'bg-emerald-100 text-emerald-700';
      default: return 'bg-yellow-100 text-yellow-700';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed': return 'Completed';
      case 'in_progress': return 'In Progress';
      case 'accepted': return 'Scheduled';
      default: return 'Pending';
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="animate-spin text-[#1a5c3a] mx-auto mb-4" size={40} />
          <p className="text-gray-500">Loading job details...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated - show loading while redirecting
  if (!isAuthenticated || !token) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="animate-spin text-[#1a5c3a] mx-auto mb-4" size={40} />
          <p className="text-gray-500">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  if (error && !job) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="mx-auto text-red-500 mb-4" size={48} />
          <p className="text-gray-600">{error}</p>
          <button
            onClick={() => navigate('/staff/dashboard')}
            className="mt-4 px-4 py-2 bg-[#1a5c3a] text-white rounded-lg"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!job) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-[#1a5c3a] text-white shadow-lg">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/staff/dashboard')}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft size={24} />
            </button>
            <img 
              src="https://customer-assets.emergentagent.com/job_trusted-maintenance-1/artifacts/l9fyih4f_Whisk_4dd066d44f7c3e88b4a47b0cc96820dddr-removebg-preview.png" 
              alt="Sancur Services" 
              className="h-10 w-auto brightness-0 invert"
            />
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Status Badge */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className={`px-4 py-2 rounded-full font-medium ${getStatusColor(job.status)}`}>
              {getStatusText(job.status)}
            </span>
            {(job.source === 'airbnb' || /airbnb/i.test(job.service_type || '')) && (
              <span className="px-3 py-2 rounded-full font-medium bg-rose-500 text-white text-sm" data-testid="job-airbnb-badge">🏡 Airbnb</span>
            )}
          </div>
          <span className="text-sm text-gray-500">
            ID: {job.id.slice(0, 8)}...
          </span>
        </div>

        {isAirbnbJob(job) && (
          <StaffAirbnbChecklist job={job} token={token} onChange={onChecklistChange} />
        )}

        <StaffJobPhotos job={job} token={token} />

        {/* Timer Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Clock className="text-[#1a5c3a]" size={20} />
            Work Timer
          </h2>
          
          <div className="text-center mb-6">
            {job.status === 'completed' ? (
              /* Completed State - Read Only */
              <>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-full mb-4">
                  <CheckCircle size={18} />
                  <span className="font-medium">Finished</span>
                </div>
                <div className="text-5xl font-mono font-bold text-gray-400 mb-2" data-testid="timer-display">
                  {job.worked_seconds ? formatTime(job.worked_seconds) : formatTime(elapsedTime)}
                </div>
                <p className="text-sm text-gray-500">Total time worked</p>
              </>
            ) : (
              /* Active State - With Controls */
              <>
                <div className="text-5xl font-mono font-bold text-[#1a5c3a] mb-4" data-testid="timer-display">
                  {formatTime(elapsedTime)}
                </div>
                
                <div className="flex items-center justify-center gap-3">
                  {!timerRunning ? (
                    <button
                      onClick={startTimer}
                      className="flex items-center gap-2 px-6 py-3 bg-[#1a5c3a] text-white rounded-xl font-medium hover:bg-[#0f3d26] transition-colors"
                      data-testid="start-timer-btn"
                    >
                      <Play size={20} />
                      Start
                    </button>
                  ) : (
                    <button
                      onClick={pauseTimer}
                      className="flex items-center gap-2 px-6 py-3 bg-yellow-500 text-white rounded-xl font-medium hover:bg-yellow-600 transition-colors"
                      data-testid="pause-timer-btn"
                    >
                      <Pause size={20} />
                      Pause
                    </button>
                  )}
                  <button
                    onClick={finishJob}
                    disabled={isAirbnbJob(job) && !checklistOk}
                    title={isAirbnbJob(job) && !checklistOk ? 'Complete a checklist Airbnb primeiro' : ''}
                    className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="finish-timer-btn"
                  >
                    <CheckCircle size={20} />
                    Finish
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Estimated Time & Job Details Card - IMPORTANT INFO FOR STAFF */}
        {(job.estimated_time || job.property_type || job.bedrooms !== undefined) && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl shadow-sm border border-blue-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Clock className="text-blue-600" size={20} />
              Tempo Estimado & Detalhes
            </h2>
            
            {/* Estimated Time - PROMINENT */}
            {job.estimated_time && (
              <div className="bg-white rounded-xl p-4 mb-4 border-2 border-blue-300">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-600 font-medium mb-1">Tempo Estimado para o Cliente</p>
                    <p className="text-3xl font-bold text-blue-700">{job.estimated_time}</p>
                    <p className="text-xs text-gray-500 mt-1">Este é o tempo que foi dado ao cliente</p>
                  </div>
                  <div className="text-5xl">⏱️</div>
                </div>
              </div>
            )}
            
            {/* Property Details Grid */}
            <div className="grid grid-cols-2 gap-3">
              {job.property_type && (
                <div className="bg-white rounded-lg p-3">
                  <p className="text-xs text-gray-500">Tipo de Imóvel</p>
                  <p className="text-sm font-medium text-gray-900 capitalize">{job.property_type}</p>
                </div>
              )}
              {job.bedrooms !== undefined && job.bedrooms !== null && (
                <div className="bg-white rounded-lg p-3">
                  <p className="text-xs text-gray-500">Quartos</p>
                  <p className="text-sm font-medium text-gray-900">{job.bedrooms}</p>
                </div>
              )}
              {job.bathrooms !== undefined && job.bathrooms !== null && (
                <div className="bg-white rounded-lg p-3">
                  <p className="text-xs text-gray-500">Casas de Banho</p>
                  <p className="text-sm font-medium text-gray-900">{job.bathrooms}</p>
                </div>
              )}
              {job.living_rooms !== undefined && job.living_rooms !== null && (
                <div className="bg-white rounded-lg p-3">
                  <p className="text-xs text-gray-500">Salas</p>
                  <p className="text-sm font-medium text-gray-900">{job.living_rooms}</p>
                </div>
              )}
              {job.has_kitchen && (
                <div className="bg-white rounded-lg p-3">
                  <p className="text-xs text-gray-500">Cozinha</p>
                  <p className="text-sm font-medium text-green-600">✓ Incluída</p>
                </div>
              )}
              {job.has_pets && (
                <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                  <p className="text-xs text-amber-600">Animais de Estimação</p>
                  <p className="text-sm font-medium text-amber-700">🐾 Sim - Usar produtos pet-safe</p>
                </div>
              )}
            </div>
            
            {/* Special Requests */}
            {job.special_requests && (
              <div className="mt-3 bg-amber-50 rounded-lg p-3 border-l-4 border-amber-400">
                <p className="text-xs text-amber-700 font-medium mb-1">⚠️ Pedidos Especiais do Cliente</p>
                <p className="text-sm text-gray-700">{job.special_requests}</p>
              </div>
            )}
          </div>
        )}

        {/* Location Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <MapPin className="text-[#1a5c3a]" size={20} />
            Location
          </h2>
          
          <div className="space-y-3">
            {job.address && (
              <div className="flex items-start gap-3">
                <Home className="text-gray-400 mt-0.5" size={18} />
                <div>
                  <p className="text-sm text-gray-500">Address</p>
                  <p className="text-gray-900">{job.address}</p>
                </div>
              </div>
            )}
            
            {job.eircode && (
              <div className="flex items-start gap-3">
                <MapPin className="text-gray-400 mt-0.5" size={18} />
                <div>
                  <p className="text-sm text-gray-500">Eircode</p>
                  <p className="text-gray-900 font-mono">{job.eircode}</p>
                </div>
              </div>
            )}
            
            {(job.address || job.eircode) && (
              <button
                onClick={openInMaps}
                className="w-full mt-4 flex items-center justify-center gap-2 px-4 py-3 bg-[#1a5c3a] text-white rounded-xl font-medium hover:bg-[#0f3d26] transition-colors"
                data-testid="open-maps-btn"
              >
                <ExternalLink size={18} />
                Open in Google Maps
              </button>
            )}
          </div>
        </div>

        {/* Client Info Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <User className="text-[#1a5c3a]" size={20} />
            Client Information
          </h2>
          
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <User className="text-gray-400" size={18} />
              <div>
                <p className="text-sm text-gray-500">Name</p>
                <p className="text-gray-900">{job.name}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Team Info Card - Show if multiple staff assigned */}
        {job.assigned_staff_names && job.assigned_staff_names.length > 1 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <User className="text-[#1a5c3a]" size={20} />
              Team
            </h2>
            
            <div className="space-y-3">
              {job.assigned_staff_names.map((name, idx) => {
                const isLeader = job.team_leader_id === job.assigned_staff_ids?.[idx];
                return (
                  <div key={idx} className={`flex items-center gap-3 p-3 rounded-xl ${isLeader ? 'bg-amber-50 border border-amber-200' : 'bg-gray-50'}`}>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isLeader ? 'bg-amber-200 text-amber-700' : 'bg-[#1a5c3a]/10 text-[#1a5c3a]'}`}>
                      {isLeader ? '⭐' : <User size={18} />}
                    </div>
                    <div>
                      <p className="text-gray-900 font-medium">{name}</p>
                      {isLeader && (
                        <p className="text-xs text-amber-600 font-medium">Team Leader</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Schedule Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Calendar className="text-[#1a5c3a]" size={20} />
            Schedule
          </h2>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-gray-50 rounded-xl">
              <p className="text-sm text-gray-500 mb-1">Date</p>
              <p className="text-gray-900 font-medium">{job.preferred_date}</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-xl">
              <p className="text-sm text-gray-500 mb-1">Time</p>
              <p className="text-gray-900 font-medium">{job.preferred_time}</p>
            </div>
          </div>
        </div>

        {/* Instructions Card */}
        {(job.message || job.notes || job.staff_instructions) && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <FileText className="text-[#1a5c3a]" size={20} />
              Instructions
            </h2>
            
            {job.staff_instructions && (
              <div className="mb-4 p-4 bg-[#1a5c3a]/10 border border-[#1a5c3a]/20 rounded-xl">
                <p className="text-sm font-medium text-[#1a5c3a] mb-1">From Manager:</p>
                <p className="text-gray-700 whitespace-pre-wrap">{job.staff_instructions}</p>
              </div>
            )}
            
            {(job.message || job.notes) && (
              <div className="p-4 bg-gray-50 rounded-xl">
                <p className="text-sm font-medium text-gray-500 mb-1">Client Notes:</p>
                <p className="text-gray-700 whitespace-pre-wrap">{job.message || job.notes}</p>
              </div>
            )}
          </div>
        )}

        {/* Staff Observations Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <MessageSquare className="text-[#1a5c3a]" size={20} />
            My Observations
          </h2>
          
          {job.status === 'completed' ? (
            /* Read-only when completed */
            <div className="p-4 bg-gray-50 rounded-xl">
              {staffNotes ? (
                <p className="text-gray-700 whitespace-pre-wrap">{staffNotes}</p>
              ) : (
                <p className="text-gray-400 italic">No observations recorded</p>
              )}
            </div>
          ) : (
            /* Editable when not completed */
            <>
              <textarea
                value={staffNotes}
                onChange={(e) => setStaffNotes(e.target.value)}
                placeholder="Add notes about this job... (e.g., what was done, any issues, feedback from client)"
                className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-[#1a5c3a] focus:border-transparent resize-none"
                rows={4}
                data-testid="staff-notes-input"
              />
              
              <div className="flex items-center justify-between mt-3">
                <div className="text-sm">
                  {notesMessage && (
                    <span className={notesMessage.includes('Error') ? 'text-red-600' : 'text-green-600'}>
                      {notesMessage}
                    </span>
                  )}
                </div>
                <button
                  onClick={saveStaffNotes}
                  disabled={savingNotes}
                  className="flex items-center gap-2 px-4 py-2 bg-[#1a5c3a] text-white rounded-lg hover:bg-[#0f3d26] transition-colors disabled:opacity-50"
                  data-testid="save-notes-btn"
                >
                  {savingNotes ? (
                    <Loader2 className="animate-spin" size={16} />
                  ) : (
                    <Save size={16} />
                  )}
                  Save Notes
                </button>
              </div>
            </>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col gap-3 pb-6">
          {job.status === 'completed' ? (
            /* Completed - No more actions available */
            <div className="text-center py-6 bg-green-50 rounded-2xl border border-green-200">
              <CheckCircle className="mx-auto text-green-600 mb-3" size={48} />
              <p className="text-green-700 font-semibold text-lg">Job Completed</p>
              <p className="text-green-600 text-sm mt-1">
                {job.worked_seconds ? `Total time: ${formatTime(job.worked_seconds)}` : 'Time recorded'}
              </p>
            </div>
          ) : (
            <>
              {job.status !== 'in_progress' && (
                <button
                  onClick={() => updateJobStatus('in_progress')}
                  className="w-full py-4 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                  data-testid="start-job-btn"
                >
                  <Play size={20} />
                  Start Job
                </button>
              )}
              
              {job.status === 'in_progress' && (
                <button
                  onClick={() => updateJobStatus('completed')}
                  className="w-full py-4 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                  data-testid="complete-job-btn"
                >
                  <CheckCircle size={20} />
                  Mark as Completed
                </button>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default StaffJobDetails;
